// engine-exception: auth/onboarding flow, not a resource screen — stays hand-written (blueprint §1: web/src/pages is legacy)
import { useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { AuthCard, AuthScene, SignedInLine } from '../components/authui';
import { CreateOrgScreen } from './CreateOrgScreen';
import { VerifyDomainScreen } from './VerifyDomainScreen';
import { SetupShopScreen } from './SetupShopScreen';

// The post-login onboarding gate. The real app renders only once the signed-in user belongs
// to an ACTIVE tenant; otherwise we drive them through founding an org (create → verify) or
// explain why they can't. It's a pure reflection of GET /v1/onboarding.

import { INVITE_TOKEN_KEY, invitePath } from '../lib/invite';
import { OAUTH_REQUEST_KEY } from '../modules/platform/OAuthConsentPage';
export { INVITE_TOKEN_KEY };

export interface DnsRecord {
  type: 'TXT';
  host: string;
  value: string;
}

export type OnboardingResponse =
  | { state: 'active'; tenant_id: string; tenant_status: 'active' | 'pending' }
  | { state: 'awaiting_verification'; domain: string; tenant_id: string; dns_record: DnsRecord }
  | { state: 'provisioning' }
  | { state: 'rejected'; reject_code: string; reject_detail?: string }
  | { state: 'new'; email: string; domain: string };

function readOAuthRequest(): string | null {
  try {
    return sessionStorage.getItem(OAUTH_REQUEST_KEY);
  } catch {
    return null;
  }
}

// Signed-in-as line wired to the auth context, for the onboarding cards.
export function SignedInAs() {
  const { session, signOut } = useAuth();
  return <SignedInLine email={session?.user?.email ?? undefined} onSignOut={() => void signOut()} />;
}

const REJECT_MESSAGES: Record<string, { title: string; body: string }> = {
  public_email: {
    title: 'Use your work email',
    body: 'NovaPSA organizations are founded from a company domain. Personal providers (Gmail, Outlook, …) can’t create one — sign out and sign back in with your work email.',
  },
  domain_taken: {
    title: 'Your company is already here',
    body: 'An organization already exists for your domain. Ask an administrator there to invite you — their email is above.',
  },
  domain_pending: {
    title: 'Setup already in progress',
    body: 'Someone at your company has started setting up your organization. Ask them to invite you once the domain is verified.',
  },
};

export function OnboardingGate({ children }: { children: ReactNode }) {
  const { api, session } = useAuth();
  const qc = useQueryClient();

  // Pending invite (arrived via /invite before signing in): finish it BEFORE anything else renders —
  // an invitee must never be shown org founding (CreateOrgScreen) on the way.
  const pending = session && typeof localStorage !== 'undefined' ? localStorage.getItem(INVITE_TOKEN_KEY) : null;

  const query = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => api.request<OnboardingResponse>('/v1/onboarding'),
    enabled: !pending,
    // Session tier once active (docs/architecture.md "Caching"); polled only while provisioning.
    staleTime: (q) => (q.state.data?.state === 'active' ? Infinity : 0),
    refetchInterval: (q) => (q.state.data?.state === 'provisioning' ? 2000 : false),
  });

  // Final onboarding step: an active tenant with no technicians hasn't answered "what do
  // you charge per hour?" — show Set up your shop once (skippable, remembered per tenant).
  const isActive = query.data?.state === 'active' && query.data.tenant_status === 'active';
  const tenantId = query.data?.state === 'active' ? query.data.tenant_id : null;
  const skipKey = tenantId ? `hf.setup_skipped.${tenantId}` : null;
  const [skipped, setSkipped] = useState(false);
  const setup = useQuery({
    queryKey: ['setup-check'],
    queryFn: () => api.listTechnicians({ limit: 1 }),
    enabled: isActive,
    staleTime: Infinity, // answered once per session; finishing setup invalidates ['setup-check']
  });

  if (pending) return <Navigate to={invitePath(pending)} replace />;
  // Back to an app's OAuth consent the user had to sign in for (modules/platform/OAuthConsentPage).
  const oauthRequest = session ? readOAuthRequest() : null;
  if (oauthRequest) return <Navigate to={`/oauth/consent?request=${encodeURIComponent(oauthRequest)}`} replace />;

  if (query.isLoading) {
    return (
      <AuthScene>
        <AuthCard>
          <div style={{ textAlign: 'center', padding: 'var(--sp-4) 0' }}>
            <div className="progress-line" style={{ maxWidth: 220, margin: '0 auto var(--sp-3)' }} />
            <p className="muted">Loading your workspace…</p>
          </div>
        </AuthCard>
      </AuthScene>
    );
  }

  if (query.error || !query.data) {
    return (
      <AuthScene>
        <AuthCard title="We couldn’t load your account" subtitle="This is usually a momentary connection issue." footer={<SignedInAs />}>
          <div className="auth-msg err">⚠ {query.error instanceof Error ? query.error.message : 'Please try again.'}</div>
          <button className="btn primary block lift" style={{ marginTop: 'var(--sp-3)' }} onClick={() => void query.refetch()}>
            Try again
          </button>
        </AuthCard>
      </AuthScene>
    );
  }

  const data = query.data;

  switch (data.state) {
    case 'active': {
      if (data.tenant_status !== 'active') return <VerifyDomainScreen />;
      // Hold until the setup check answers, so the app never flashes before the last step.
      if (setup.isLoading) {
        return (
          <AuthScene>
            <AuthCard>
              <div style={{ textAlign: 'center', padding: 'var(--sp-4) 0' }}>
                <div className="progress-line" style={{ maxWidth: 220, margin: '0 auto var(--sp-3)' }} />
                <p className="muted">Loading your workspace…</p>
              </div>
            </AuthCard>
          </AuthScene>
        );
      }
      const hasTechs = (setup.data?.data.length ?? 0) > 0;
      const skipStored = skipKey ? localStorage.getItem(skipKey) === '1' : false;
      if (!hasTechs && !skipped && !skipStored && !setup.isError) {
        return (
          <SetupShopScreen
            onDone={() => {
              qc.invalidateQueries({ queryKey: ['setup-check'] });
              qc.invalidateQueries({ queryKey: ['technicians'] });
              qc.invalidateQueries({ queryKey: ['stats'] });
            }}
            onSkip={() => {
              if (skipKey) localStorage.setItem(skipKey, '1');
              setSkipped(true);
            }}
          />
        );
      }
      return <>{children}</>;
    }

    case 'awaiting_verification':
      return <VerifyDomainScreen domain={data.domain} dnsRecord={data.dns_record} />;

    case 'provisioning':
      return (
        <AuthScene>
          <AuthCard title="Setting up your organization" subtitle="This takes just a moment.">
            <div className="progress-line" style={{ maxWidth: 260, margin: 'var(--sp-2) auto var(--sp-3)' }} />
            <p className="muted" style={{ textAlign: 'center' }}>
              We’re creating your workspace and preparing your domain verification…
            </p>
          </AuthCard>
        </AuthScene>
      );

    case 'new':
      return <CreateOrgScreen email={data.email} domain={data.domain} />;

    case 'rejected': {
      const m = REJECT_MESSAGES[data.reject_code] ?? {
        title: 'We can’t continue',
        body: data.reject_detail ?? 'You can’t create an organization with this account.',
      };
      return (
        <AuthScene>
          <AuthCard title={m.title} footer={<SignedInAs />}>
            <p className="muted" style={{ textAlign: 'center', lineHeight: 1.5 }}>{m.body}</p>
          </AuthCard>
        </AuthScene>
      );
    }

    default:
      return null;
  }
}
